import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import PanoramaOutlinedIcon from '@mui/icons-material/PanoramaOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';
import { InstagramGlyph, TiktokGlyph, YoutubeGlyph } from '../../components/BuzzBridge/SocialPlatformSvgs';
import { BrandSocialIntegration } from '../../components/BrandSocialIntegration';
import { CropperModal } from '../../components/ImageCropper/ImageCropperModal';
import { RectangularImageCropperModal } from '../../components/ImageCropper/RectangularImageCropperModal';
import {
    addBrand,
    getHostDashboard,
    updateBrand,
    brandSocialLogin,
    getBrand,
    createBrandSocialLink,
    updateBrandSocialLink,
} from '../../redux/buzzbridge/actions';
import { useDispatchPromise, showToastMessage } from '../../utils/AppUtils';
import type { ISocialLinks, ICreateBrand, IBrandSocialProfile } from '../../redux/buzzbridge/types';
import {
    brandManualSocialLinkFromProfile,
    brandManualSocialLinkToApi,
    type IBrandManualSocialLinkData,
} from '../../utils/brandManualSocialLinkUtils';

const VERTICALS: { id: string; label: string; industry: string; Icon: typeof PaletteOutlinedIcon }[] = [
    { id: 'creative', label: 'Creative', industry: 'Art & Design', Icon: PaletteOutlinedIcon },
    { id: 'lifestyle', label: 'Lifestyle', industry: 'Lifestyle', Icon: ShoppingBagOutlinedIcon },
    { id: 'tech', label: 'Tech', industry: 'Technology & Electronics', Icon: BoltOutlinedIcon },
    { id: 'other', label: 'Other', industry: 'Entertainment & Media', Icon: MoreHorizIcon },
];

const STEPS = [
    { n: '01', label: 'Brand profile' },
    { n: '02', label: 'Social integration' },
    { n: '03', label: 'Funding account' },
];

export interface BrandOnboardingModalProps {
    open: boolean;
    onClose: () => void;
    initialData?: {
        _id?: string;
        name?: string;
        industry?: string;
        logo?: string;
        feature_image?: string;
        website?: string;
        description?: string;
        social_links?: Partial<ISocialLinks>;
        social_profiles?: IBrandSocialProfile[];
    } | null;
    /** Navigate to wallet page after completing step 3. */
    onGoToWallet?: () => void;
}

type PreviewPlatform = 'instagram' | 'tiktok' | 'youtube';

function normalizeSocialPlatform(platform: string): PreviewPlatform | null {
    const x = platform.toLowerCase().replace(/-/g, '_');
    if (x === 'instagram') return 'instagram';
    if (x === 'tiktok' || x === 'tik_tok') return 'tiktok';
    if (x === 'youtube') return 'youtube';
    return null;
}

function isPreviewPlatformLinked(
    platform: PreviewPlatform,
    profiles:
        | { platform: string; linked?: boolean; url?: string; username?: string; display_name?: string }[]
        | undefined
        | null,
    connected: Partial<ISocialLinks>,
): boolean {
    const list = profiles ?? undefined;
    if (
        list?.some(
            p => normalizeSocialPlatform(p.platform) === platform && Boolean(p.url || p.username || p.display_name),
        )
    ) {
        return true;
    }
    if (list?.some(p => normalizeSocialPlatform(p.platform) === platform && p.linked === true)) return true;
    const v = connected[platform];
    if (typeof v !== 'string' || !v.trim()) return false;
    return !v.endsWith('_pending');
}

function socialsFromProfiles(
    profiles?: { platform: string; username?: string; display_name?: string; linked?: boolean; url?: string }[],
): Partial<ISocialLinks> {
    if (!profiles?.length) return {};
    const out: Partial<ISocialLinks> = {};
    for (const p of profiles) {
        const key = normalizeSocialPlatform(p.platform);
        if (!key) continue;
        const hasManual = p.linked === false && Boolean(p.url?.trim());
        const hasOAuth = p.linked === true;
        if (hasOAuth || hasManual || p.url || p.username || p.display_name) {
            (out as Record<string, string>)[key] = p.username || p.display_name || p.url || p.platform;
        }
    }
    return out;
}

function buildBrandSocialStateFromProfiles(profiles?: IBrandSocialProfile[]): {
    manualLinkIds: Partial<Record<PreviewPlatform, string>>;
    initialManualLinks: Partial<Record<PreviewPlatform, IBrandManualSocialLinkData>>;
    oauthLinkedPlatforms: Partial<Record<PreviewPlatform, boolean>>;
} {
    const manualLinkIds: Partial<Record<PreviewPlatform, string>> = {};
    const initialManualLinks: Partial<Record<PreviewPlatform, IBrandManualSocialLinkData>> = {};
    const oauthLinkedPlatforms: Partial<Record<PreviewPlatform, boolean>> = {};

    for (const p of profiles ?? []) {
        const key = normalizeSocialPlatform(p.platform);
        if (!key) continue;
        if (p._id) manualLinkIds[key] = p._id;
        if (p.linked === true) {
            oauthLinkedPlatforms[key] = true;
        } else if (p.url) {
            initialManualLinks[key] = brandManualSocialLinkFromProfile(p);
            oauthLinkedPlatforms[key] = false;
        }
    }

    return { manualLinkIds, initialManualLinks, oauthLinkedPlatforms };
}

function verticalFromIndustry(industry?: string): string {
    if (!industry) return 'other';
    const lower = industry.toLowerCase();
    if (
        lower.includes('creative') ||
        lower.includes('art') ||
        lower.includes('design') ||
        lower.includes('music') ||
        lower.includes('film')
    )
        return 'creative';
    if (
        lower.includes('lifestyle') ||
        lower.includes('fashion') ||
        lower.includes('beauty') ||
        lower.includes('food') ||
        lower.includes('travel')
    )
        return 'lifestyle';
    if (
        lower.includes('tech') ||
        lower.includes('gaming') ||
        lower.includes('software') ||
        lower.includes('electronics')
    )
        return 'tech';
    return 'other';
}

export function BrandOnboardingModal({ open, onClose, initialData, onGoToWallet }: BrandOnboardingModalProps) {
    const { device } = useThemeContext();
    const dispatch = useDispatchPromise();
    const fullScreen = device.isMobile;
    const fileRef = useRef<HTMLInputElement>(null);
    const featureFileRef = useRef<HTMLInputElement>(null);
    const [step, setStep] = useState(0);
    const [brandName, setBrandName] = useState('');
    const [vertical, setVertical] = useState('creative');
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [featureImagePreview, setFeatureImagePreview] = useState<string | null>(null);
    const [featureFile, setFeatureFile] = useState<File | null>(null);
    const [connectedSocials, setConnectedSocials] = useState<Partial<ISocialLinks>>({});
    const [oauthLinkedPlatforms, setOauthLinkedPlatforms] = useState<Partial<Record<PreviewPlatform, boolean>>>({});
    const [manualLinkIds, setManualLinkIds] = useState<Partial<Record<PreviewPlatform, string>>>({});
    const [initialManualLinks, setInitialManualLinks] = useState<
        Partial<Record<PreviewPlatform, IBrandManualSocialLinkData>>
    >({});
    const [savingManualPlatform, setSavingManualPlatform] = useState<keyof ISocialLinks | null>(null);
    const [connectingPlatform, setConnectingPlatform] = useState<keyof ISocialLinks | null>(null);
    const [, setPendingOAuthCodes] = useState<{ code: string; provider: string; platformId: keyof ISocialLinks }[]>([]);
    const [createdBrandId, setCreatedBrandId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Cropper state
    const [logoCropSrc, setLogoCropSrc] = useState<string | null>(null);
    const [logoCropOpen, setLogoCropOpen] = useState(false);
    const [featureCropSrc, setFeatureCropSrc] = useState<string | null>(null);
    const [featureCropOpen, setFeatureCropOpen] = useState(false);

    useEffect(() => {
        if (open) {
            setStep(0);
            setBrandName(initialData?.name ?? '');
            setVertical(verticalFromIndustry(initialData?.industry));
            setLogoPreview(initialData?.logo ?? null);
            setLogoFile(null);
            setFeatureImagePreview(initialData?.feature_image ?? null);
            setFeatureFile(null);
            const socialState = buildBrandSocialStateFromProfiles(
                initialData?.social_profiles as IBrandSocialProfile[],
            );
            setConnectedSocials(socialsFromProfiles(initialData?.social_profiles));
            setOauthLinkedPlatforms(socialState.oauthLinkedPlatforms);
            setManualLinkIds(socialState.manualLinkIds);
            setInitialManualLinks(socialState.initialManualLinks);
            setSavingManualPlatform(null);
            setConnectingPlatform(null);
            setPendingOAuthCodes([]);
            setCreatedBrandId(initialData?._id ?? null);
            setSaving(false);
        }
    }, [open, initialData]);

    const onLogoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setLogoCropSrc(URL.createObjectURL(f));
        setLogoCropOpen(true);
        e.target.value = '';
    }, []);

    const onFeatureImageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFeatureCropSrc(URL.createObjectURL(f));
        setFeatureCropOpen(true);
        e.target.value = '';
    }, []);

    const progress = Math.round(((step + 1) / STEPS.length) * 100);

    const previewPlatformLinked = useMemo(
        () => ({
            instagram: isPreviewPlatformLinked('instagram', initialData?.social_profiles, connectedSocials),
            tiktok: isPreviewPlatformLinked('tiktok', initialData?.social_profiles, connectedSocials),
            youtube: isPreviewPlatformLinked('youtube', initialData?.social_profiles, connectedSocials),
        }),
        [initialData?.social_profiles, connectedSocials],
    );

    const handleDisconnect = (id: keyof ISocialLinks) => {
        setConnectedSocials(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setOauthLinkedPlatforms(prev => {
            const next = { ...prev };
            delete next[id as PreviewPlatform];
            return next;
        });
        setManualLinkIds(prev => {
            const next = { ...prev };
            delete next[id as PreviewPlatform];
            return next;
        });
        setInitialManualLinks(prev => {
            const next = { ...prev };
            delete next[id as PreviewPlatform];
            return next;
        });
        setPendingOAuthCodes(prev => prev.filter(p => p.platformId !== id));
    };

    const brandId = createdBrandId || initialData?._id || null;

    const handleConnecting = useCallback((id: keyof ISocialLinks) => {
        setConnectingPlatform(id);
    }, []);

    const handleOAuthCode = useCallback(
        (code: string, provider: string, platformId: keyof ISocialLinks) => {
            if (brandId) {
                dispatch(brandSocialLogin({ brand_id: brandId, code, provider }))
                    .then((res: any) => {
                        const link = res?.data?.data?.brand_social_link;
                        const username = link?.username || link?.display_name || platformId;
                        setConnectedSocials(prev => ({ ...prev, [platformId]: username }));
                        setOauthLinkedPlatforms(prev => ({ ...prev, [platformId as PreviewPlatform]: true }));
                        dispatch(getBrand(brandId));
                    })
                    .catch(() => {})
                    .finally(() => setConnectingPlatform(null));
            } else {
                setConnectedSocials(prev => ({ ...prev, [platformId]: `${platformId}_pending` }));
                setPendingOAuthCodes(prev => [
                    ...prev.filter(p => p.platformId !== platformId),
                    { code, provider, platformId },
                ]);
                setConnectingPlatform(null);
            }
        },
        [dispatch, brandId],
    );

    const handleManualSocialSave = useCallback(
        async (platformId: keyof ISocialLinks, data: IBrandManualSocialLinkData) => {
            if (!brandId) {
                showToastMessage('Complete step 1 to create your brand first.', 'error');
                throw new Error('Brand id required');
            }
            setSavingManualPlatform(platformId);
            try {
                const apiBody = brandManualSocialLinkToApi(platformId as 'instagram' | 'tiktok' | 'youtube', data);
                const existingId = manualLinkIds[platformId as PreviewPlatform];
                const link = (
                    existingId
                        ? await dispatch(
                              updateBrandSocialLink({
                                  brandId,
                                  linkId: existingId,
                                  payload: apiBody,
                              }),
                          )
                        : await dispatch(
                              createBrandSocialLink({
                                  brandId,
                                  payload: { platform: String(platformId), ...apiBody },
                              }),
                          )
                ) as IBrandSocialProfile;

                if (link?._id) {
                    setManualLinkIds(prev => ({ ...prev, [platformId as PreviewPlatform]: link._id! }));
                }
                const label = data.username.trim();
                setConnectedSocials(prev => ({ ...prev, [platformId]: label }));
                setOauthLinkedPlatforms(prev => ({ ...prev, [platformId as PreviewPlatform]: false }));
                setInitialManualLinks(prev => ({ ...prev, [platformId as PreviewPlatform]: data }));
                dispatch(getBrand(brandId));
            } finally {
                setSavingManualPlatform(null);
            }
        },
        [brandId, manualLinkIds, dispatch],
    );

    const canAdvance =
        step === 0
            ? brandName.trim().length > 0 && vertical.length > 0
            : step === 1
            ? Object.keys(connectedSocials).length > 0
            : true;

    const handleNext = useCallback(async () => {
        if (step >= STEPS.length - 1 || !canAdvance || saving) return;

        if (step === 0) {
            setSaving(true);
            const industry = VERTICALS.find(v => v.id === vertical)?.industry ?? vertical;
            try {
                if (brandId) {
                    await dispatch(
                        updateBrand({
                            documentId: brandId,
                            payload: {
                                name: brandName.trim(),
                                industry,
                                ...(logoFile && { logo: [logoFile] }),
                                ...(featureFile && { feature_image: [featureFile] }),
                            },
                        }),
                    );
                } else {
                    const payload: ICreateBrand = {
                        name: brandName.trim(),
                        industry: industry as ICreateBrand['industry'],
                        is_default: true,
                        ...(logoFile && { rawLogo: [logoFile] }),
                        ...(featureFile && { rawFeatureImage: [featureFile] }),
                    };
                    const data: any = await dispatch(addBrand(payload));
                    setCreatedBrandId(data._id);
                    if (data?._id) {
                        dispatch(getHostDashboard({ brandId: data._id }));
                    }
                }
                setStep(1);
            } catch {
                // globalErrorHandler in saga already shows toast
            } finally {
                setSaving(false);
            }
            return;
        }

        setStep(s => s + 1);
    }, [step, canAdvance, saving, brandId, brandName, vertical, logoFile, featureFile, dispatch]);
    const handleBack = () => {
        if (step > 0) setStep(s => s - 1);
        else onClose();
    };

    const handleFinish = () => {
        onClose();
        onGoToWallet?.();
    };

    const titleText =
        step === 0
            ? initialData
                ? 'Edit brand profile'
                : 'Brand profile'
            : step === 1
            ? 'Social integration'
            : 'Funding account';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            maxWidth="lg"
            fullWidth
            scroll="paper"
            aria-labelledby="brand-onboarding-title"
            PaperProps={{
                'data-skip-brand-profile-gate': 'true',
                sx: {
                    ...(fullScreen
                        ? {
                              m: 0,
                              maxHeight: '100%',
                              height: '100%',
                              width: '100%',
                              maxWidth: '100%',
                              borderRadius: 0,
                              border: 'none',
                          }
                        : {
                              maxHeight: 'min(92vh, 900px)',
                              borderRadius: 2,
                              border: '1px solid rgba(255,255,255,0.08)',
                          }),
                    bgcolor: '#0e0e0e',
                    backgroundImage: 'linear-gradient(180deg, rgba(26,25,25,0.98) 0%, #0e0e0e 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            {/* Title bar */}
            <DialogTitle
                id="brand-onboarding-title"
                sx={{
                    py: 1.5,
                    px: { xs: 1.5, sm: 2 },
                    pt: { xs: 'calc(12px + env(safe-area-inset-top, 0px))', sm: 1.5 },
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexShrink: 0,
                }}>
                <Typography
                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 17, color: '#fff', letterSpacing: '-0.02em' }}>
                    {titleText}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, justifyContent: 'center' }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 10,
                            color: '#adaaaa',
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            display: { xs: 'none', sm: 'block' },
                        }}>
                        Onboarding progress
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {STEPS.map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: i <= step ? BRAND_LUXE_SECONDARY : '#262626',
                                    boxShadow: i <= step ? '0 0 12px rgba(0,253,193,0.35)' : 'none',
                                }}
                            />
                        ))}
                    </Box>
                </Box>
                <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: '#adaaaa' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Content */}
            <DialogContent
                sx={{
                    position: 'relative',
                    overflow: 'auto',
                    py: 2,
                    px: { xs: 1.5, sm: 3 },
                    flex: 1,
                    minHeight: 0,
                    WebkitOverflowScrolling: 'touch',
                }}>
                {/* Ambient glow */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '-5%',
                        right: '-10%',
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: 'rgba(255,201,101,0.06)',
                        filter: 'blur(60px)',
                        pointerEvents: 'none',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: '-5%',
                        width: 160,
                        height: 160,
                        borderRadius: '50%',
                        background: 'rgba(0,253,193,0.05)',
                        filter: 'blur(50px)',
                        pointerEvents: 'none',
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 960, mx: 'auto' }}>
                    {/* Step header + progress */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2 }}>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 10,
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    color: BRAND_LUXE_SECONDARY,
                                    mb: 0.5,
                                }}>
                                Step {STEPS[step].n}/03
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 22,
                                    color: '#fff',
                                    letterSpacing: '-0.02em',
                                }}>
                                {STEPS[step].label}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: 12, color: '#adaaaa', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                Completion
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 20,
                                    color: BRAND_LUXE_PRIMARY,
                                    fontWeight: 900,
                                }}>
                                {progress}%
                            </Typography>
                        </Box>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 4,
                            borderRadius: 999,
                            bgcolor: '#262626',
                            mb: 3,
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 999,
                                background: BRAND_LUXE_SECONDARY,
                                boxShadow: '0 0 12px rgba(0,253,193,0.5)',
                            },
                        }}
                    />

                    {/* === STEP 1: Brand profile === */}
                    {step === 0 && (
                        <Box
                            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' }, gap: 2 }}>
                            <Box
                                sx={{ gridColumn: { md: 'span 7' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 2.5, borderRadius: 2 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 16,
                                            color: BRAND_LUXE_PRIMARY,
                                            mb: 1,
                                        }}>
                                        Identity &amp; branding
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 13,
                                            color: '#adaaaa',
                                            mb: 2,
                                            lineHeight: 1.5,
                                        }}>
                                        Define how your brand appears across the ecosystem.
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 11,
                                            letterSpacing: '0.12em',
                                            textTransform: 'uppercase',
                                            color: '#adaaaa',
                                            mb: 1,
                                        }}>
                                        Brand name
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={brandName}
                                        onChange={e => setBrandName(e.target.value)}
                                        placeholder="e.g. Neon Horizon Media"
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                                borderRadius: 2,
                                                color: '#fff',
                                                '& fieldset': { border: 'none' },
                                                '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(236,170,0,0.4)' },
                                            },
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 11,
                                            letterSpacing: '0.12em',
                                            textTransform: 'uppercase',
                                            color: '#adaaaa',
                                            mb: 1,
                                        }}>
                                        Industry vertical
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                        {VERTICALS.map(({ id, label, Icon }) => {
                                            const active = vertical === id;
                                            return (
                                                <Button
                                                    key={id}
                                                    size="small"
                                                    onClick={() => setVertical(id)}
                                                    startIcon={<Icon sx={{ fontSize: 20 }} />}
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        py: 1.25,
                                                        px: 1.5,
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 13,
                                                        border: active
                                                            ? '1px solid rgba(255,201,101,0.35)'
                                                            : '1px solid transparent',
                                                        bgcolor: active
                                                            ? BRAND_SURFACE_CONTAINER
                                                            : BRAND_SURFACE_CONTAINER_HIGH,
                                                        color: active ? BRAND_LUXE_PRIMARY : '#adaaaa',
                                                    }}>
                                                    {label}
                                                </Button>
                                            );
                                        })}
                                    </Box>
                                </Box>

                                {/* Combined feature image + logo uploader */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        borderRadius: 2,
                                        overflow: 'visible',
                                        mb: 3,
                                    }}>
                                    <input ref={fileRef} type="file" accept="image/*" hidden onChange={onLogoChange} />
                                    <input
                                        ref={featureFileRef}
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={onFeatureImageChange}
                                    />
                                    {/* Feature image area */}
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => featureFileRef.current?.click()}
                                        sx={{
                                            width: '100%',
                                            height: 140,
                                            borderRadius: 2,
                                            border: '2px dashed rgba(72,72,71,0.45)',
                                            bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            font: 'inherit',
                                            color: 'inherit',
                                            overflow: 'hidden',
                                            p: 0,
                                            '&:hover': { borderColor: 'rgba(255,201,101,0.4)' },
                                        }}>
                                        {featureImagePreview ? (
                                            <Box
                                                component="img"
                                                src={featureImagePreview}
                                                alt=""
                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <Box sx={{ textAlign: 'center' }}>
                                                <PanoramaOutlinedIcon
                                                    sx={{ fontSize: 28, color: BRAND_LUXE_PRIMARY, mb: 0.5 }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 13,
                                                        color: '#fff',
                                                    }}>
                                                    Upload feature image
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: '#adaaaa' }}>
                                                    1200×400px recommended
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Logo — layered, centred at the bottom of the feature image */}
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={e => {
                                            e.stopPropagation();
                                            fileRef.current?.click();
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            left: '50%',
                                            bottom: 0,
                                            transform: 'translate(-50%, 50%)',
                                            width: 72,
                                            height: 72,
                                            borderRadius: '50%',
                                            border: `3px solid ${BRAND_SURFACE_CONTAINER_LOW}`,
                                            bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            p: 0,
                                            font: 'inherit',
                                            color: 'inherit',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                                            transition: 'border-color 0.2s',
                                            '&:hover': { borderColor: BRAND_LUXE_PRIMARY },
                                        }}>
                                        {logoPreview ? (
                                            <Box
                                                component="img"
                                                src={logoPreview}
                                                alt=""
                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <UploadFileOutlinedIcon sx={{ fontSize: 24, color: BRAND_LUXE_PRIMARY }} />
                                        )}
                                    </Box>
                                </Box>
                                <Typography
                                    sx={{
                                        textAlign: 'center',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 11,
                                        color: '#adaaaa',
                                        mt: 1,
                                    }}>
                                    Click banner to change feature image · Click logo to change brand logo
                                </Typography>
                            </Box>

                            <Box
                                sx={{ gridColumn: { md: 'span 5' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Live preview card */}
                                <Box
                                    sx={{
                                        bgcolor: BRAND_SURFACE_CONTAINER,
                                        borderRadius: 2,
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}>
                                    {/* Preview badge */}
                                    <Typography
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            zIndex: 2,
                                            fontSize: 10,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.14em',
                                            bgcolor: 'rgba(0,253,193,0.1)',
                                            color: BRAND_LUXE_SECONDARY,
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 999,
                                        }}>
                                        Preview
                                    </Typography>

                                    {/* Feature banner */}
                                    <Box
                                        sx={{
                                            height: 100,
                                            background: featureImagePreview
                                                ? `url(${featureImagePreview}) center/cover no-repeat`
                                                : 'linear-gradient(135deg, rgba(255,193,7,0.12) 0%, rgba(0,230,195,0.06) 100%)',
                                        }}
                                    />

                                    {/* Logo overlay + details */}
                                    <Box sx={{ px: 2, pb: 2, mt: -3.5 }}>
                                        <Box
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                borderRadius: '50%',
                                                border: `3px solid ${BRAND_SURFACE_CONTAINER}`,
                                                bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden',
                                                mx: 'auto',
                                                mb: 1,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                            }}>
                                            {logoPreview ? (
                                                <Box
                                                    component="img"
                                                    src={logoPreview}
                                                    alt=""
                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <ImageOutlinedIcon sx={{ color: '#767575', fontSize: 24 }} />
                                            )}
                                        </Box>

                                        <Typography
                                            noWrap
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 16,
                                                color: brandName ? '#fff' : 'rgba(255,255,255,0.2)',
                                                textAlign: 'center',
                                                mb: 0.25,
                                            }}>
                                            {brandName || 'Brand name'}
                                        </Typography>

                                        {vertical && (
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: 11,
                                                    color: BRAND_LUXE_PRIMARY,
                                                    textAlign: 'center',
                                                    textTransform: 'capitalize',
                                                    mb: 1,
                                                }}>
                                                {VERTICALS.find(v => v.id === vertical)?.label ?? vertical}
                                            </Typography>
                                        )}

                                        {/* Social platform preview (linked = check, else lock) */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                pt: 0.5,
                                            }}
                                            aria-label="Connected social platforms preview">
                                            {(
                                                [
                                                    {
                                                        id: 'instagram' as const,
                                                        Glyph: InstagramGlyph,
                                                    },
                                                    { id: 'tiktok' as const, Glyph: TiktokGlyph },
                                                    { id: 'youtube' as const, Glyph: YoutubeGlyph },
                                                ] as const
                                            ).map(({ id, Glyph }) => {
                                                const linked = previewPlatformLinked[id];
                                                return (
                                                    <Box
                                                        key={id}
                                                        sx={{
                                                            position: 'relative',
                                                            width: 22,
                                                            height: 22,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}>
                                                        <Glyph
                                                            sx={{
                                                                width: 18,
                                                                height: 18,
                                                                color: '#8a8888',
                                                            }}
                                                        />
                                                        {linked ? (
                                                            <CheckCircleIcon
                                                                sx={{
                                                                    position: 'absolute',
                                                                    right: -2,
                                                                    bottom: -2,
                                                                    fontSize: 11,
                                                                    color: BRAND_LUXE_SECONDARY,
                                                                    bgcolor: BRAND_SURFACE_CONTAINER,
                                                                    borderRadius: '50%',
                                                                }}
                                                            />
                                                        ) : (
                                                            <LockIcon
                                                                sx={{
                                                                    position: 'absolute',
                                                                    right: -3,
                                                                    bottom: -2,
                                                                    fontSize: 9,
                                                                    color: 'rgba(255,255,255,0.35)',
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                </Box>

                                {/* AI optimizer hint */}
                                <Box
                                    sx={{
                                        bgcolor: 'rgba(255,201,101,0.05)',
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid rgba(255,201,101,0.1)',
                                    }}>
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <AutoAwesomeOutlinedIcon
                                            sx={{ color: BRAND_LUXE_PRIMARY, flexShrink: 0, fontSize: 22 }}
                                        />
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    fontSize: 13,
                                                    color: BRAND_LUXE_PRIMARY,
                                                    mb: 0.5,
                                                }}>
                                                Midnight AI optimizer
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: '#adaaaa', lineHeight: 1.5 }}>
                                                Your brand profile is used to match you with top-tier creators. Use
                                                descriptive names for better matching accuracy.
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Step list sidebar */}
                                <StepList currentStep={step} />
                            </Box>
                        </Box>
                    )}

                    {/* === STEP 2: Social integration === */}
                    {step === 1 && (
                        <BrandSocialIntegration
                            brandId={brandId}
                            connectedLinks={connectedSocials}
                            oauthLinkedPlatforms={oauthLinkedPlatforms}
                            connectingPlatform={connectingPlatform}
                            savingManualPlatform={savingManualPlatform}
                            initialManualLinks={initialManualLinks}
                            onConnecting={handleConnecting}
                            onDisconnect={handleDisconnect}
                            onOAuthCode={handleOAuthCode}
                            onAuthFailed={() => setConnectingPlatform(null)}
                            onManualSave={handleManualSocialSave}
                            onSkip={() => setStep(s => s + 1)}
                        />
                    )}

                    {/* === STEP 3: Funding account === */}
                    {step === 2 && (
                        <Box sx={{ textAlign: 'center', py: { xs: 3, md: 5 }, maxWidth: 420, mx: 'auto' }}>
                            <Box
                                sx={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(255,201,101,0.08)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2,
                                }}>
                                <SavingsOutlinedIcon sx={{ fontSize: 32, color: BRAND_LUXE_PRIMARY }} />
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 18,
                                    color: '#fff',
                                    mb: 0.75,
                                    fontWeight: 600,
                                }}>
                                Set up your funding account
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    color: '#adaaaa',
                                    lineHeight: 1.6,
                                    mb: 2.5,
                                }}>
                                Save your progress and head to the{' '}
                                <span style={{ color: BRAND_LUXE_PRIMARY, fontWeight: 600 }}>Wallet</span> to add a
                                payment method, fund campaigns, and manage payouts.
                            </Typography>
                            <Button
                                onClick={handleFinish}
                                startIcon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18 }} />}
                                sx={{
                                    px: 3,
                                    py: 1,
                                    borderRadius: 999,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    color: '#392700',
                                    background: 'linear-gradient(to right, #ffc965, #feb700)',
                                    boxShadow: '0 8px 24px rgba(254,183,0,0.25)',
                                    '&:hover': { filter: 'brightness(1.05)', transform: 'scale(1.02)' },
                                }}>
                                Save &amp; go to Wallet
                            </Button>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* Footer actions */}
            <DialogActions
                sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: 1.5,
                    pb: { xs: 'calc(12px + env(safe-area-inset-bottom, 0px))', sm: 1.5 },
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    background: 'rgba(26,25,25,0.85)',
                    backdropFilter: 'blur(12px)',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    gap: 1,
                }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{
                        color: '#adaaaa',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                        '&:hover': { color: '#fff' },
                    }}>
                    Back
                </Button>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                        sx={{
                            px: 2,
                            py: 1,
                            borderRadius: 999,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            color: '#adaaaa',
                            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.05)' },
                        }}>
                        Save draft
                    </Button>
                    {step < STEPS.length - 1 && (
                        <Button
                            onClick={handleNext}
                            disabled={!canAdvance || saving}
                            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#392700' }} /> : undefined}
                            sx={{
                                px: 3,
                                py: 1,
                                borderRadius: 999,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: '#392700',
                                background: 'linear-gradient(to right, #ffc965, #feb700)',
                                boxShadow: '0 10px 30px rgba(254,183,0,0.3)',
                                '&:hover': { filter: 'brightness(1.05)', transform: 'scale(1.02)' },
                                '&.Mui-disabled': {
                                    background: 'rgba(255,201,101,0.25)',
                                    color: 'rgba(57,39,0,0.4)',
                                    boxShadow: 'none',
                                },
                            }}>
                            {saving
                                ? brandId
                                    ? 'Updating brand...'
                                    : 'Creating brand...'
                                : `Continue to step ${step + 2}`}
                        </Button>
                    )}
                    {step === STEPS.length - 1 && (
                        <Button
                            onClick={handleFinish}
                            sx={{
                                px: 3,
                                py: 1,
                                borderRadius: 999,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: '#392700',
                                background: 'linear-gradient(to right, #ffc965, #feb700)',
                                boxShadow: '0 10px 30px rgba(254,183,0,0.3)',
                                '&:hover': { filter: 'brightness(1.05)', transform: 'scale(1.02)' },
                            }}>
                            Save &amp; go to Wallet
                        </Button>
                    )}
                </Box>
            </DialogActions>

            {/* Circular logo cropper */}
            {logoCropSrc && (
                <CropperModal
                    src={logoCropSrc}
                    modalOpen={logoCropOpen}
                    setModalOpen={setLogoCropOpen}
                    setPreview={val => {
                        if (typeof val === 'function') {
                            setLogoPreview(val);
                            return;
                        }
                        setLogoPreview(val);
                        if (val) {
                            fetch(val)
                                .then(r => r.blob())
                                .then(blob => setLogoFile(new File([blob], 'logo.jpg', { type: blob.type })));
                        }
                    }}
                />
            )}

            {/* Rectangular feature image cropper (3:1 landscape) */}
            {featureCropSrc && (
                <RectangularImageCropperModal
                    open={featureCropOpen}
                    src={featureCropSrc}
                    aspect={3}
                    cropHeight={260}
                    onSave={(base64, file) => {
                        setFeatureImagePreview(base64);
                        setFeatureFile(file);
                        setFeatureCropOpen(false);
                    }}
                    onCancel={() => setFeatureCropOpen(false)}
                />
            )}
        </Dialog>
    );
}

/** Sidebar step indicator shared across steps. */
function StepList({ currentStep }: { currentStep: number }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {STEPS.map((s, i) => {
                const active = i === currentStep;
                const done = i < currentStep;
                return (
                    <Box
                        key={s.n}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: active ? BRAND_SURFACE_CONTAINER_LOW : 'rgba(19,19,19,0.4)',
                            opacity: active || done ? 1 : 0.55,
                            borderLeft: active ? `4px solid ${BRAND_LUXE_SECONDARY}` : '4px solid transparent',
                        }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 900,
                                color: active ? BRAND_LUXE_SECONDARY : '#adaaaa',
                            }}>
                            {s.n}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                color: active ? '#fff' : '#adaaaa',
                                flex: 1,
                            }}>
                            {s.label}
                        </Typography>
                        {done && <CheckCircleIcon sx={{ fontSize: 18, color: BRAND_LUXE_SECONDARY }} />}
                    </Box>
                );
            })}
        </Box>
    );
}
